/**
 * CutterSelectorMatrix.jsx — Interactive cutter recommender on the
 * raiseboring-cutters landing.
 *
 * User picks their rock condition (Formation label / UCS band / CAI band)
 * from a chip row. The component highlights the matching column, marks
 * recommended cutters with a check, and shows a short "Recommended for
 * your rock" panel with links to the cutter PDPs.
 *
 * Source data: `src/data/cutterSelectorMatrix.js`, extracted verbatim
 * from Sandvik's marketing PNG (Rotary raiseboring folder).
 *
 * @module components/raiseboring/CutterSelectorMatrix
 * @since 2026-07-08 (feat/content-enrichment D2 follow-up)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Info } from 'lucide-react';
import {
  CUTTER_TIERS,
  CUTTER_RECOMMENDATIONS,
  GLOSSARY,
} from '@data/cutterSelectorMatrix';

export default function CutterSelectorMatrix() {
  const [selectedTier, setSelectedTier] = useState(null);   // tier id or null
  const [showGlossary, setShowGlossary] = useState(false);

  const recommended = selectedTier
    ? CUTTER_RECOMMENDATIONS.filter((c) => c.matches.includes(selectedTier))
    : [];

  return (
    <section className="mb-10 border border-n2 bg-white">
      <div className="px-5 py-4 border-b border-n2 bg-n1/40 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black">
            Cutter selection guide
          </h2>
          <p className="text-n7 font-sandvik-text text-sm mt-1">
            Match your rock condition to Sandvik's recommended raiseboring cutter.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowGlossary((v) => !v)}
          className="flex-shrink-0 inline-flex items-center gap-1 text-electric-blue text-sm font-sandvik-text hover:underline"
        >
          <Info size={14} />
          {showGlossary ? 'Hide' : 'What are UCS & CAI?'}
        </button>
      </div>

      {showGlossary && (
        <div className="px-5 py-4 border-b border-n2 bg-sandvik-warning/10 text-sm font-sandvik-text text-carbon-black space-y-2">
          <p><strong>UCS</strong> — {GLOSSARY.UCS}</p>
          <p><strong>CAI</strong> — {GLOSSARY.CAI}</p>
        </div>
      )}

      <div className="p-5">
        <p className="text-xs tracking-wide text-n5 font-sandvik-text mb-2">
          Select your rock condition
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {CUTTER_TIERS.map((tier) => {
            const isActive = selectedTier === tier.id;
            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => setSelectedTier(isActive ? null : tier.id)}
                className={`px-4 py-2 border text-sm font-sandvik-text transition-colors ${
                  isActive
                    ? 'bg-electric-blue text-white border-electric-blue'
                    : 'bg-white text-carbon-black border-n2 hover:border-electric-blue hover:text-electric-blue'
                }`}
              >
                <div className="font-medium leading-tight">{tier.formation}</div>
                <div className={`text-xs mt-0.5 ${isActive ? 'text-white/80' : 'text-n5'}`}>
                  UCS {tier.ucs.display}
                </div>
              </button>
            );
          })}
        </div>

        {/* Matrix — always visible so the user sees the full picture even
            without a selection. Selected column highlights in gold. */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sandvik-text border-collapse min-w-[720px]">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border border-n2 bg-n1 font-medium text-carbon-black">
                  Cutter
                </th>
                {CUTTER_TIERS.map((tier) => (
                  <th
                    key={tier.id}
                    className={`px-3 py-2 border border-n2 font-medium text-center align-top ${
                      selectedTier === tier.id ? 'bg-sandvik-gold/20' : 'bg-n1'
                    }`}
                  >
                    <div className="text-carbon-black">{tier.label}</div>
                    <div className="text-xs text-n5 font-normal mt-0.5">
                      {tier.formation}
                    </div>
                    <div className="text-xs text-n5 font-normal">
                      CAI {tier.cai.display}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CUTTER_RECOMMENDATIONS.map((cutter) => (
                <tr key={cutter.id}>
                  <td className="px-3 py-3 border border-n2 align-top">
                    <div className="font-medium text-carbon-black">{cutter.label}</div>
                    <div className="text-xs text-n5 mt-1 leading-relaxed">
                      {cutter.description}
                    </div>
                  </td>
                  {CUTTER_TIERS.map((tier) => {
                    const isRec = cutter.matches.includes(tier.id);
                    const isSelectedCol = selectedTier === tier.id;
                    return (
                      <td
                        key={tier.id}
                        className={`px-3 py-3 border border-n2 text-center align-middle ${
                          isSelectedCol
                            ? isRec ? 'bg-sandvik-success/20' : 'bg-sandvik-gold/10'
                            : ''
                        }`}
                      >
                        {isRec && (
                          <Check
                            size={20}
                            className={`inline-block ${isSelectedCol ? 'text-sandvik-success' : 'text-electric-blue'}`}
                            aria-label="Recommended"
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedTier && recommended.length > 0 && (
          <div className="mt-5 p-4 border-l-4 border-sandvik-success bg-sandvik-success/10">
            <p className="text-sm font-sandvik-text text-carbon-black mb-2">
              Recommended cutter{recommended.length > 1 ? 's' : ''} for{' '}
              {CUTTER_TIERS.find((t) => t.id === selectedTier)?.formation.toLowerCase()} /{' '}
              {CUTTER_TIERS.find((t) => t.id === selectedTier)?.label.toLowerCase()} rock:
            </p>
            <div className="flex flex-wrap gap-3">
              {recommended.map((c) => (
                <div key={c.id} className="flex flex-col gap-1">
                  <span className="font-sandvik-text text-carbon-black">
                    {c.label}
                  </span>
                  <div className="flex gap-2 flex-wrap">
                    {c.itemCodes.map((code) => (
                      <Link
                        key={code}
                        to={`/products/detail/${code}`}
                        className="text-xs text-electric-blue font-sandvik-text hover:underline"
                      >
                        {code} ›
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTier && recommended.length === 0 && (
          <p className="mt-5 text-sm text-n5 font-sandvik-text italic">
            No specific recommendation matches this rock condition — please contact us for a bespoke consultation.
          </p>
        )}
      </div>
    </section>
  );
}
